import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut,
  updateProfile,
  getAdditionalUserInfo,
} from 'firebase/auth';
import { auth, googleProvider, facebookProvider, githubProvider } from '../utils/firebase';
import { track } from '../utils/analytics';
import { beginLoadSession, abandonLoadSession } from '../utils/timing';

// Sign-ins are counted by the API, from the auth_time claim in the token the
// caller already sends, not written from here.
//
// This used to be a setDoc into users/{uid}. That document is what the grace
// window is metered against, and firestore.rules lets a user write their own —
// so the number deciding how long a lapsed subscriber keeps working was stored
// somewhere they could set it, and could be stopped altogether by blocking one
// request. See api/logins.py.

// A popup sign-in is the same call whether the account existed or not, so the
// only thing that distinguishes an acquisition from a returning visit is
// Firebase's own isNewUser flag on the credential. Reading it wrongly would put
// every Google login in the sign-up count.
function recordSocialSignUp(cred, method) {
  if (getAdditionalUserInfo(cred)?.isNewUser) track('sign_up', { method });
}

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser]             = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  useEffect(() => {
    // The first callback is Firebase resolving whatever session the page
    // started with; any later one that produces a user is somebody signing in
    // with the app already open. The two feel completely different to the
    // person waiting, so the timing report is told which it was rather than
    // left to infer it from how long the page had been alive.
    let resolvedOnce = false;
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) beginLoadSession(resolvedOnce ? 'interactive-signin' : 'session-restored');
      else abandonLoadSession();
      resolvedOnce = true;
      setUser(u);
      setEmailVerified(u?.emailVerified || false);
      setLoading(false);
    });
    return unsub;
  }, []);

  // Refreshes the Firebase user token so emailVerified reflects the latest state.
  // Call this after the user clicks the verification link and returns to the app.
  const reloadUser = useCallback(async () => {
    if (!auth.currentUser) return;
    const wasVerified = auth.currentUser.emailVerified;
    await auth.currentUser.reload();
    // Force a token refresh so Firestore's email_verified claim is current.
    // reload() updates the local user object but not the JWT; without this,
    // writes that require isEmailVerified() fail until the token auto-rotates.
    await auth.currentUser.getIdToken(true);
    // Only the transition is worth an event. This runs on every return to the
    // app from the verification link and again after an invite redemption, so
    // logging unconditionally would count one verification several times.
    if (auth.currentUser.emailVerified && !wasVerified) track('email_verified');
    setEmailVerified(auth.currentUser.emailVerified);
  }, []);

  const resendVerificationEmail = useCallback(async () => {
    if (!auth.currentUser) return;
    try { await sendEmailVerification(auth.currentUser); }
    catch (e) { /* silently ignore — most likely a rate-limit */ }
  }, []);

  const clearError = useCallback(() => setError(''), []);

  const loginWithGoogle = useCallback(async () => {
    try { clearError(); const c = await signInWithPopup(auth, googleProvider); recordSocialSignUp(c, 'google'); }
    catch (e) { setError(friendlyError(e)); }
  }, [clearError]);

  const loginWithFacebook = useCallback(async () => {
    try { clearError(); const c = await signInWithPopup(auth, facebookProvider); recordSocialSignUp(c, 'facebook'); }
    catch (e) { setError(friendlyError(e)); }
  }, [clearError]);

  const loginWithGithub = useCallback(async () => {
    try { clearError(); const c = await signInWithPopup(auth, githubProvider); recordSocialSignUp(c, 'github'); }
    catch (e) { setError(friendlyError(e)); }
  }, [clearError]);

  const loginWithEmail = useCallback(async (email, password) => {
    try { clearError(); const c = await signInWithEmailAndPassword(auth, email, password); }
    catch (e) { setError(friendlyError(e)); }
  }, [clearError]);

  const registerWithEmail = useCallback(async (email, password, displayName) => {
    try {
      clearError();
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) await updateProfile(cred.user, { displayName });
      track('sign_up', { method: 'password' });
    } catch (e) { setError(friendlyError(e)); return; }
    // Send verification email separately so account-creation errors and
    // email-send errors surface independently. Log failures visibly during
    // development so they aren't silently swallowed.
    try {
      await sendEmailVerification(auth.currentUser);
    } catch (e) {
      console.error('[Auth] sendEmailVerification failed:', e.code, e.message);
    }
  }, [clearError]);

  const resetPassword = useCallback(async (email) => {
    try {
      clearError();
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (e) {
      setError(friendlyError(e));
      return false;
    }
  }, [clearError]);

  const logout = useCallback(async () => {
    try { await signOut(auth); }
    catch (e) { setError(friendlyError(e)); }
  }, []);

  // Memoized, and every function above with it.
  //
  // Consumers put these in dependency arrays. TreeContext's bootstrap effect
  // reaches reloadUser through acceptInviteLink and re-runs whenever its
  // identity changes, and that effect raises a loading spinner it cannot always
  // lower — so an unmemoized function here surfaced as a permanently spinning
  // app on a new account, not as a wasted render. Rebuilding this object on
  // every render did the same thing to anything depending on the context value
  // as a whole.
  const value = useMemo(() => ({
    user, emailVerified, loading, error, clearError,
    loginWithGoogle, loginWithFacebook, loginWithGithub,
    loginWithEmail, registerWithEmail, resetPassword, logout,
    reloadUser, resendVerificationEmail,
  }), [
    user, emailVerified, loading, error, clearError,
    loginWithGoogle, loginWithFacebook, loginWithGithub,
    loginWithEmail, registerWithEmail, resetPassword, logout,
    reloadUser, resendVerificationEmail,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function friendlyError(e) {
  const code = e?.code || '';
  const map = {
    'auth/user-not-found':            'No account found with that email.',
    'auth/wrong-password':            'Incorrect password.',
    'auth/invalid-credential':        'Invalid email or password.',
    'auth/email-already-in-use':      'An account already exists with that email.',
    'auth/weak-password':             'Password should be at least 6 characters.',
    'auth/invalid-email':             'Please enter a valid email address.',
    'auth/popup-closed-by-user':      'Sign-in popup was closed. Try again.',
    'auth/account-exists-with-different-credential': 'An account already exists with a different sign-in method.',
    'auth/popup-blocked':             'Popup was blocked by your browser. Please allow popups.',
    'auth/cancelled-popup-request':   '',
  };
  return map[code] || e?.message || 'An error occurred. Please try again.';
}
